import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { Product, QueryProductDto } from "../../../Redux/ecommersProduct/types";
import { useAppDispatch } from "../../../Redux/hooks";
import { fetchProducts } from "../../../Redux/ecommersProduct/slice";

interface HomePageContextType {
  productsByCategory: Record<string, Product[]>;
  loadingByCategory: Record<string, boolean>;
  errorByCategory: Record<string, string | null>;
  fetchCategoryProducts: (category: string) => Promise<void>;
  selectedProduct: Product | null;
  setSelectedProduct: (product: Product | null) => void;
}

const HomePageContext = createContext<HomePageContextType | undefined>(
  undefined,
);

export const HomePageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const [productsByCategory, setProductsByCategory] = useState<
    Record<string, Product[]>
  >({});
  const [loadingByCategory, setLoadingByCategory] = useState<
    Record<string, boolean>
  >({});
  const [errorByCategory, setErrorByCategory] = useState<
    Record<string, string | null>
  >({});
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const fetchCategoryProducts = useCallback(
    async (category: string) => {
      setLoadingByCategory((prev) => ({ ...prev, [category]: true }));
      setErrorByCategory((prev) => ({ ...prev, [category]: null }));

      try {
        const queryParams: QueryProductDto = {
          superCategory: category,
          limit: 12,
        };

        const resultAction = await dispatch(fetchProducts(queryParams));

        if (fetchProducts.fulfilled.match(resultAction)) {
          setProductsByCategory((prev) => ({
            ...prev,
            [category]: resultAction.payload.data,
          }));
        } else {
          setErrorByCategory((prev) => ({
            ...prev,
            [category]:
              (resultAction.payload as string) || "Failed to fetch products",
          }));
        }
      } catch (err) {
        setErrorByCategory((prev) => ({
          ...prev,
          [category]: "An unexpected error occurred",
        }));
      } finally {
        setLoadingByCategory((prev) => ({ ...prev, [category]: false }));
      }
    },
    [dispatch],
  );

  return (
    <HomePageContext.Provider
      value={{
        productsByCategory,
        loadingByCategory,
        errorByCategory,
        fetchCategoryProducts,
        selectedProduct,
        setSelectedProduct,
      }}
    >
      {children}
    </HomePageContext.Provider>
  );
};

export const useHomePageContext = () => {
  const context = useContext(HomePageContext);
  if (context === undefined) {
    throw new Error(
      "useHomePageContext must be used within a HomePageProvider",
    );
  }
  return context;
};
